import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import { Check } from 'lucide-react';

/**
 * 토스페이먼츠 successUrl 콜백
 * URL: /premium/success?paymentKey=xxx&orderId=yyy&amount=2900
 */
export function PremiumSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    const paymentKey = params.get('paymentKey');
    const orderId = params.get('orderId');
    const amount = Number(params.get('amount') ?? 0);

    if (!paymentKey || !orderId || !amount) {
      setStatus('error');
      setError('결제 정보가 올바르지 않아요.');
      return;
    }

    (async () => {
      try {
        await post('/premium/confirm', { paymentKey, orderId, amount });
        setStatus('success');
      } catch (err: any) {
        setStatus('error');
        setError(err?.response?.data?.error?.message ?? '결제 승인에 실패했어요.');
      }
    })();
  }, [params]);

  if (status === 'processing') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
        <Spinner size="lg" />
        <p className="text-sm text-[var(--color-text-sub)]">결제를 승인하는 중이에요...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="mx-auto max-w-md px-5 py-16 text-center">
        <div className="text-5xl">❌</div>
        <h1 className="mt-4 text-2xl font-bold">결제 실패</h1>
        <p className="mt-2 text-sm text-[var(--color-text-sub)]">{error}</p>
        <Button className="mt-6" onClick={() => navigate('/premium')}>
          다시 시도
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-5 py-16 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-primary)]/10">
        <Check className="h-8 w-8 text-[var(--color-primary)]" />
      </div>
      <h1 className="mt-6 text-3xl font-extrabold">프리미엄 가입 완료!</h1>
      <p className="mt-2 text-sm text-[var(--color-text-sub)]">
        이제 모든 프리미엄 혜택을 이용하실 수 있어요.
      </p>
      <div className="mt-8 flex gap-2">
        <Button variant="secondary" fullWidth onClick={() => navigate('/premium')}>
          구독 정보
        </Button>
        <Button fullWidth onClick={() => navigate('/')}>
          홈으로
        </Button>
      </div>
    </div>
  );
}
